import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import { ArrowRightIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { WidgetComponent } from '../../types/widgets.types'
import { WidgetWrapper } from '../WidgetWrapper'
import {
  ANIMATION_FRAME_DELAY,
  BASE_WIDGET_HEIGHT,
  BASE_WIDGET_WIDTH,
  DEFAULT_CONTAINER_SIZE,
  DEFAULT_TOP_OFFSET,
  HOVER_OFFSET_X_LEFT,
  HOVER_OFFSET_X_RIGHT,
  HOVER_OFFSET_Y,
  ONBOARDING_WIDGET_CARD_LABELS,
  SMALL_VIEW_BREAKPOINT,
  SMALL_VIEW_TOP_OFFSET,
  SPRING_TRANSITION
} from './OnboardingWidget.constant'
import styles from './OnboardingWidget.module.scss'
import { DiscoverAppsModal } from './components'
import backgroundImage from '/public/widgets/tips-widget.webp'
import foregroundImage from '/public/widgets/tips.png'

const waitForLayout = (
  callback: () => void,
  frames = ANIMATION_FRAME_DELAY
) => {
  let remainingFrames = frames
  const tick = () => {
    remainingFrames -= 1
    if (remainingFrames > 0) {
      requestAnimationFrame(tick)
    } else {
      callback()
    }
  }
  requestAnimationFrame(tick)
}

export const OnboardingWidget: WidgetComponent = ({ ...widgetProps }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'onboarding_widget'
  })
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imageLoadedRef = useRef(false)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const measureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isDiscoverAppsModalOpen, setIsDiscoverAppsModalOpen] = useState(false)
  const [containerSize, setContainerSize] = useState(DEFAULT_CONTAINER_SIZE)

  const handleDiscoverAppsModalOpen = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()
      setIsDiscoverAppsModalOpen(true)
    },
    []
  )

  const handleCloseModal = useCallback(() => {
    setIsDiscoverAppsModalOpen(false)
  }, [])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const measureContainerRef = useRef<() => void>()

  measureContainerRef.current = () => {
    if (!imageLoadedRef.current) return

    const container = containerRef.current
    if (!container) return

    if (measureTimeoutRef.current) {
      clearTimeout(measureTimeoutRef.current)
    }

    measureTimeoutRef.current = setTimeout(() => {
      waitForLayout(() => {
        const { width, height } = container.getBoundingClientRect()
        if (width > 0 && height > 0) {
          setContainerSize(prevSize => {
            if (prevSize.width !== width || prevSize.height !== height) {
              return { width, height }
            }
            return prevSize
          })
        }
      })
    }, 16)
  }

  const handleImageLoad = useCallback(() => {
    imageLoadedRef.current = true
    measureContainerRef.current?.()
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        measureContainerRef.current?.()
      })
    }

    resizeObserverRef.current.observe(container)

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
      if (measureTimeoutRef.current) {
        clearTimeout(measureTimeoutRef.current)
      }
    }
  }, [])

  const { labelPositions, scaleFactor } = useMemo(() => {
    const widthScale = containerSize.width / BASE_WIDGET_WIDTH
    const heightScale = containerSize.height / BASE_WIDGET_HEIGHT
    const isSmallView = containerSize.width < SMALL_VIEW_BREAKPOINT
    const topOffset = isSmallView ? SMALL_VIEW_TOP_OFFSET : DEFAULT_TOP_OFFSET

    const positions = ONBOARDING_WIDGET_CARD_LABELS.map((label, index) => {
      const hoverOffsetX =
        index === 0 || index === 1
          ? HOVER_OFFSET_X_LEFT
          : index === 3 || index === 4
            ? HOVER_OFFSET_X_RIGHT
            : 0

      return {
        ...label,
        position: {
          top: `${
            label.basePosition.top * heightScale + topOffset * heightScale
          }px`,
          left: `${label.basePosition.left * widthScale}px`
        },
        hoverOffsetX,
        hoverOffsetY: HOVER_OFFSET_Y
      }
    })

    return {
      labelPositions: positions,
      scaleFactor: { width: widthScale, height: heightScale }
    }
  }, [containerSize])

  return (
    <>
      <DiscoverAppsModal
        isOpen={isDiscoverAppsModalOpen}
        onClose={handleCloseModal}
      />
      <WidgetWrapper {...widgetProps}>
        <div
          ref={containerRef}
          className={styles.container}
          onClick={handleDiscoverAppsModalOpen}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <motion.div
            className={styles.background}
            style={{
              backgroundImage: `url(${backgroundImage})`
            }}
            initial={{
              x: '-50%',
              y: '-50%',
              scale: 1,
              filter: 'brightness(1.2) contrast(0.9) saturate(0.9)'
            }}
            animate={{
              x: '-50%',
              y: '-50%',
              scale: isHovered ? 1.1 : 1,
              filter: isHovered
                ? 'brightness(0.85) contrast(1.15) saturate(1.2)'
                : 'brightness(1.2) contrast(0.9) saturate(0.9)'
            }}
            transition={SPRING_TRANSITION}
          />

          <motion.div
            className={styles.topText}
            animate={{
              scale: isHovered ? 1.08 : 1
            }}
            transition={SPRING_TRANSITION}
          >
            {t('top_text')}
          </motion.div>

          <motion.div
            className={styles.foreground}
            animate={{
              scale: isHovered ? 1.1 : 1
            }}
            transition={SPRING_TRANSITION}
          >
            <img
              src={foregroundImage}
              alt='Clusterix onboarding'
              onLoad={handleImageLoad}
            />
          </motion.div>

          {labelPositions.map(label => (
            <motion.div
              key={label.text}
              className={cn(
                styles.cardLabel,
                label.className && styles[label.className]
              )}
              style={label.position}
              initial={{
                rotate: label.rotation,
                scale: 1,
                x: 0,
                y: 0
              }}
              animate={{
                rotate: label.rotation,
                scale: isHovered ? 1.25 : 1,
                x: isHovered ? label.hoverOffsetX * scaleFactor.width : 0,
                y: isHovered ? label.hoverOffsetY * scaleFactor.height : 0
              }}
              transition={SPRING_TRANSITION}
            >
              {label.text}
            </motion.div>
          ))}

          <div className={styles.ctaButton}>
            <Button
              trailingIcon={ArrowRightIcon}
              bold
              size={ButtonSizes.S}
              variant={ButtonVariants.Text}
              color={ButtonColors.Green}
              label={t('cta_button')}
              onClick={() => {}}
            />
          </div>
        </div>
      </WidgetWrapper>
    </>
  )
}
